/**
 * Morse — „Was kommt als Nächstes?“ für den Server. (07.10.2026, Werkzeug `morse_stand`)
 *
 * Vespera soll Martin beim Lernen begleiten und denselben Satz sagen, den die Seite im Reiter „Fortschritt“
 * zeigt. Die Seite rechnet ihn in frontend/lib/morse-texte.ts (`alsNaechstes`) und frontend/lib/morse-runde.ts
 * (`stufenSicherheit`) — Dateien, die der Server nicht lesen kann (sie hängen an DOM-Typen).
 * Darum stehen die beiden kleinen, reinen Rechnungen hier, Wort für Wort gleich.
 *
 * ⚠️ ZWEI BESITZER, EIN WÄCHTER: bis die Seite von hier liest, hält
 *    tests/frontend/morse-stand-satz.test.ts beide gleich — er ruft die Funktion der Seite UND diese auf, über alle
 *    Stufen und Lagen, und rechnet auch den Satz nach, den die Seite WIRKLICH zeichnet (`MorseFortschritt`).
 *    Ändert jemand den Satz auf einer Seite, wird er dort rot. Ziel: `frontend/lib/morse-texte.ts` und
 *    `morse-runde.ts` re-exportieren von hier, dann gibt es nur noch eine Fassung.
 */
import {
  KOCH_LETZTE_STUFE,
  KOCH_REIHENFOLGE,
  KOCH_SCHWELLE,
  kochNeueZeichen,
  kochStufe,
  kochStufenZiel,
  kochZeichen,
  type StufenAufgabe,
  type StufenZiel,
} from "./morse-koch.js";

/**
 * Wie sicher die Zeichen der Stufe sitzen: das Mittel ihrer Güte, nie gehörte zählen 0 — `null`, solange
 * keines gehört ist. Ab 0,9 im Fenster der Stufe kommt ein neues Zeichen (Koch).
 */
export function stufenSicherheit(stufe: number, zeichen: ReadonlyArray<{ zeichen: string; guete: number; gehoert: number }>): number | null {
  const je = new Map(zeichen.map((z) => [z.zeichen, z]));
  const liste = kochZeichen(stufe);
  if (!liste.some((z) => (je.get(z)?.gehoert ?? 0) > 0)) return null;
  return (
    liste.reduce((s, z) => {
      const g = je.get(z);
      return s + (g && g.gehoert > 0 ? g.guete : 0);
    }, 0) / liste.length
  );
}

/**
 * EIN Satz, was als Nächstes dran ist. `sicher`: wie sicher die Zeichen der Stufe sitzen (`null`: auf dieser Stufe
 * noch nicht gehört). `neu`: die neuen Zeichen der Stufe; `danach`: das Zeichen, das als Nächstes kommt (`null` auf
 * der letzten Stufe).
 */
export function alsNaechstes(o: {
  sicher: number | null;
  neu: readonly string[];
  danach: string | null;
  prozent: (x: number) => string;
  /** Das Ziel der Stufe (16.10.2026) — ist es da, sagt der Satz, welche Bedingung des Aufstiegs fehlt. */
  ziel?: StufenZiel | null;
  /** Wie ein Zeichen im Satz heißt (Vespera liest „Punkt (.)“ statt „.“) — Vorgabe: das Zeichen selbst. */
  name?: (z: string) => string;
}): string {
  const neu = o.neu.join(" und ");
  if (o.danach === null) return "Als Nächstes: Alle Zeichen sind dabei. Wenn du magst, stell unter „Tempo und Ton“ ein schnelleres Tempo ein.";
  if (o.sicher === null) return `Als Nächstes: ${neu} kennenlernen und die erste Hörrunde machen.`;
  // 16.10.2026: Das Mittel der Güte („sicher zu 75 %“) ist NICHT die Regel des Aufstiegs — Martin übte 200 Mal und der
  // Satz blieb gleich. Mit dem Fenster sagt er, was wirklich fehlt.
  if (o.ziel) return `Als Nächstes: ${zielLuecke(o.ziel, o.prozent, o.name)}`;
  if (o.sicher < KOCH_SCHWELLE)
    return `Als Nächstes: weiter Hörrunden, bis deine Zeichen zu ${o.prozent(KOCH_SCHWELLE)} sitzen (jetzt ${o.prozent(o.sicher)}) — dann kommt ${o.danach} dazu.`;
  return `Als Nächstes: noch eine gute Hörrunde mit ${neu}, dann kommt ${o.danach} dazu.`;
}

/**
 * Der Satz für diesen Stand — dieselbe Zusammensetzung wie `MorseFortschritt.zeichne` auf der Seite
 * (frontend/lib/morse-fortschritt.ts): die Sicherheit der Stufe, ihre neuen Zeichen, das Zeichen danach.
 */
export function naechsterSchritt(
  stand: { stufe: number; zeichen: ReadonlyArray<{ zeichen: string; guete: number; gehoert: number }>; fenster?: ReadonlyArray<StufenAufgabe> },
  prozent: (x: number) => string,
  name?: (z: string) => string,
): string {
  const st = kochStufe(stand.stufe);
  return alsNaechstes({
    sicher: stufenSicherheit(stand.stufe, stand.zeichen),
    neu: kochNeueZeichen(st),
    danach: st < KOCH_LETZTE_STUFE ? (KOCH_REIHENFOLGE[st + 1] ?? null) : null,
    prozent,
    ziel: stand.fenster ? kochStufenZiel(st, stand.fenster) : null,
    name,
  });
}

/* ── Das Ziel der Stufe in Worten (16.10.2026) ─────────────────────────
   EIN Besitzer für Seite (morse-ziel.ts, Fortschritt) und Vespera (morse_stand): was der Aufstieg verlangt, wie weit
   es ist und was gerade fehlt. Die Zahlen rechnet `kochStufenZiel` (shared/morse-koch.ts). */

/** Was gerade fehlt — ein Satz. */
export function zielLuecke(z: StufenZiel, prozent: (x: number) => string, name: (z: string) => string = (x) => x): string {
  const neu = name(z.neu);
  const danach = z.danach === null ? null : name(z.danach);
  switch (z.fehlt) {
    case "zeichen":
      return `noch ${z.mindestZeichen - z.zeichen} Zeichen hören (${z.zeichen} von ${z.mindestZeichen} gezählt, in Aufgaben mit ${neu}).`;
    case "neu-anzahl":
      return `${neu} kommt noch zu selten vor: ${z.neuGesamt} von ${z.mindestNeu} Mal in den gezählten Aufgaben.`;
    case "quote":
      return `insgesamt ${z.quote === null ? "–" : prozent(z.quote)} richtig — für ${danach ?? "den Aufstieg"} braucht es ${prozent(z.schwelle)}.`;
    case "neu-quote":
      return `${neu} sitzt noch nicht: ${z.neuQuote === null ? "–" : prozent(z.neuQuote)} richtig, es braucht ${prozent(z.schwelle)}.`;
    default:
      return `alles erfüllt — die nächste gezählte Runde bringt ${danach ?? "nichts Neues mehr"}.`;
  }
}

/** Die vier Bedingungen des Aufstiegs, je mit Stand — für die Liste auf der Seite. */
export function zielPunkte(z: StufenZiel, prozent: (x: number) => string): Array<{ art: "zeichen" | "neu-anzahl" | "quote" | "neu-quote"; ok: boolean; name: string; wert: string }> {
  // Erfüllt oder nicht: EINE Quelle (`kochStufenZiel`, `z.ok`) — hier nur die Worte dazu.
  return [
    { art: "zeichen", ok: z.ok.zeichen, name: "Genug gehört", wert: `${z.zeichen} von ${z.mindestZeichen} Zeichen` },
    { art: "neu-anzahl", ok: z.ok["neu-anzahl"], name: `${z.neu} oft genug`, wert: `${z.neuGesamt} von ${z.mindestNeu} Mal` },
    { art: "quote", ok: z.ok.quote, name: "Alle Zeichen richtig", wert: `${z.quote === null ? "–" : prozent(z.quote)} von ${prozent(z.schwelle)}` },
    { art: "neu-quote", ok: z.ok["neu-quote"], name: `${z.neu} richtig`, wert: `${z.neuQuote === null ? "–" : prozent(z.neuQuote)} von ${prozent(z.schwelle)}` },
  ];
}
